import * as React from 'react'
import {
    Box,
    Grid,
    IconButton,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'

const PLUGIN_KEY = 'attackberi'
const SETTINGS_KEY = 'berriGreenSettings'

const DEFAULT_SETTINGS = Object.freeze({
    attackCountThreshold: '300',
    senderX: '0',
    senderY: '0',
    campList: [],
    sleepBetweenCyclesSeconds: '190',
    useFeathers: false
})

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const toStringValue = (value, fallback = '') =>
    [undefined, null].includes(value) ? fallback : String(value)

const normalizeCampTarget = value => ({
    x: toStringValue(value?.x ?? value?.[0]),
    y: toStringValue(value?.y ?? value?.[1])
})

export const normalizeBerriGreenSettings = pluginState => {
    const source = pluginState?.[SETTINGS_KEY] && typeof pluginState[SETTINGS_KEY] === 'object' ?
        pluginState[SETTINGS_KEY] :
        {}

    return {
        attackCountThreshold: toStringValue(source.attackCountThreshold, DEFAULT_SETTINGS.attackCountThreshold),
        senderX: toStringValue(source.senderX, DEFAULT_SETTINGS.senderX),
        senderY: toStringValue(source.senderY, DEFAULT_SETTINGS.senderY),
        campList: Array.isArray(source.campList) ? source.campList.map(normalizeCampTarget) : cloneValue(DEFAULT_SETTINGS.campList),
        sleepBetweenCyclesSeconds: toStringValue(source.sleepBetweenCyclesSeconds, DEFAULT_SETTINGS.sleepBetweenCyclesSeconds),
        useFeathers: Boolean(source.useFeathers ?? pluginState?.useLeather ?? DEFAULT_SETTINGS.useFeathers)
    }
}

export const buildBerriGreenPluginState = (userPlugins, nextConfig) => {
    const nextPlugins = cloneValue(userPlugins ?? {})
    nextPlugins[PLUGIN_KEY] ??= {}
    nextPlugins[PLUGIN_KEY][SETTINGS_KEY] = cloneValue(nextConfig)
    nextPlugins[PLUGIN_KEY].useLeather = Boolean(nextConfig.useFeathers)
    nextPlugins[PLUGIN_KEY].useCoin = !Boolean(nextConfig.useFeathers)
    return nextPlugins
}

const cardSx = {
    borderRadius: '22px',
    border: '1px solid rgba(129, 146, 178, 0.18)',
    background: 'linear-gradient(180deg, rgba(21, 33, 55, 0.62) 0%, rgba(18, 29, 48, 0.5) 100%)',
    boxShadow: '0 14px 32px rgba(7, 13, 26, 0.18)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(92, 118, 163, 0.07) 0%, rgba(92, 118, 163, 0) 22%)',
        pointerEvents: 'none'
    }
}

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

function Helper({ children }) {
    return (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.7 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>{children}</Typography>
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

function ToggleField({ checked, onChange, label, helper }) {
    return (
        <Box sx={{ minHeight: 54 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                <Switch
                    checked={checked}
                    onChange={event => onChange(event.target.checked)}
                    sx={{
                        width: 48,
                        height: 29,
                        p: 0,
                        '& .MuiSwitch-switchBase': {
                            p: '2.5px',
                            '&.Mui-checked': {
                                transform: 'translateX(18px)',
                                color: '#ffffff'
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#2f7cff',
                                opacity: 1
                            }
                        },
                        '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                            opacity: 1,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(111, 123, 143, 0.9)'
                        }
                    }}
                />
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>{label}</Typography>
            </Stack>
            <Helper>{helper}</Helper>
        </Box>
    )
}

export default function BerriGreenSettingsPanel({ userPlugins, onChange }) {
    const pluginState = userPlugins?.[PLUGIN_KEY]
    const config = React.useMemo(() => normalizeBerriGreenSettings(pluginState), [pluginState])

    const update = React.useCallback(producer => {
        const nextConfig = normalizeBerriGreenSettings(userPlugins?.[PLUGIN_KEY])
        producer(nextConfig)

        onChange(buildBerriGreenPluginState(userPlugins, nextConfig))
    }, [onChange, userPlugins])

    const addCampTarget = () => {
        update(nextConfig => {
            nextConfig.campList.push({ x: '', y: '' })
        })
    }

    const removeCampTarget = index => {
        update(nextConfig => {
            nextConfig.campList = nextConfig.campList.filter((_, currentIndex) => currentIndex !== index)
        })
    }

    const updateCampTarget = (index, field, nextValue) => {
        update(nextConfig => {
            nextConfig.campList[index] ??= { x: '', y: '' }
            nextConfig.campList[index][field] = nextValue
        })
    }

    return (
        <Box sx={cardSx}>
            <Stack spacing={4.1} sx={{ p: { xs: 2.3, md: 3.3 }, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={1.15} alignItems="flex-start">
                    <StarBorderRoundedIcon sx={{ color: '#f5f7ff', mt: 0.22 }} />
                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.08rem', fontWeight: 800 }}>
                            Berri Green Settings
                        </Typography>
                        <Typography sx={{ color: '#9fb0cf', fontSize: '0.98rem', mt: 0.4 }}>
                            Configure green camp rotation and timing
                        </Typography>
                    </Box>
                </Stack>

                <Box>
                    <SectionHeading>Core Settings</SectionHeading>
                    <Grid container spacing={2.8}>
                        <Grid item xs={12} md={7}>
                            <FieldTitle>Attack Count Threshold</FieldTitle>
                            <TextField
                                fullWidth
                                value={config.attackCountThreshold}
                                onChange={event => update(nextConfig => { nextConfig.attackCountThreshold = event.target.value })}
                                sx={inputSx}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                            <Helper>Stop once total attacks reach this number (daily cap ~3500).</Helper>
                        </Grid>
                    </Grid>
                </Box>

                <Box>
                    <SectionHeading>Sender Coordinates</SectionHeading>
                    <Grid container spacing={2.8}>
                        <Grid item xs={12} md={6}>
                            <FieldTitle>Sender X Coordinate</FieldTitle>
                            <TextField
                                fullWidth
                                value={config.senderX}
                                onChange={event => update(nextConfig => { nextConfig.senderX = event.target.value })}
                                sx={inputSx}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                            <Helper>0 uses the main castle; otherwise must match an owned castle.</Helper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FieldTitle>Sender Y Coordinate</FieldTitle>
                            <TextField
                                fullWidth
                                value={config.senderY}
                                onChange={event => update(nextConfig => { nextConfig.senderY = event.target.value })}
                                sx={inputSx}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                            <Helper>0 uses the main castle; otherwise must match an owned castle.</Helper>
                        </Grid>
                    </Grid>
                </Box>

                <Box>
                    <SectionHeading>Camp Targets</SectionHeading>
                    <Grid container spacing={2.8}>
                        <Grid item xs={12} lg={7}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.15 }}>
                                <Box>
                                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                        Camp List <Box component="span" sx={{ color: '#ff6d7b' }}>*</Box>
                                    </Typography>
                                    <Typography sx={{ color: '#c6d0e3', fontSize: '0.94rem', mt: 0.65 }}>
                                        {`Camp List (${config.campList.length} items)`}
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={addCampTarget}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '10px',
                                        border: '1px solid rgba(129, 146, 178, 0.28)',
                                        backgroundColor: 'rgba(32, 45, 70, 0.76)',
                                        color: '#f4f7ff',
                                        '&:hover': {
                                            backgroundColor: 'rgba(42, 57, 88, 0.92)'
                                        }
                                    }}
                                >
                                    <AddRoundedIcon />
                                </IconButton>
                            </Stack>

                            <Box
                                sx={{
                                    maxHeight: 248,
                                    overflowY: 'auto',
                                    pr: 0.35
                                }}
                            >
                                <Stack spacing={1.15}>
                                    {config.campList.length > 0 ? config.campList.map((campTarget, index) => (
                                        <Box
                                            key={`berri-camp-target-${index}`}
                                            sx={{
                                                p: 0.8,
                                                borderRadius: '10px',
                                                backgroundColor: 'rgba(55, 72, 103, 0.55)',
                                                border: '1px solid rgba(129, 146, 178, 0.12)'
                                            }}
                                        >
                                            <Stack direction="row" spacing={0.9} alignItems="center">
                                                <TextField
                                                    fullWidth
                                                    value={campTarget.x}
                                                    onChange={event => updateCampTarget(index, 'x', event.target.value)}
                                                    sx={inputSx}
                                                    inputProps={{ inputMode: 'numeric' }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    value={campTarget.y}
                                                    onChange={event => updateCampTarget(index, 'y', event.target.value)}
                                                    sx={inputSx}
                                                    inputProps={{ inputMode: 'numeric' }}
                                                />
                                                <IconButton
                                                    onClick={() => removeCampTarget(index)}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '10px',
                                                        backgroundColor: '#ff3345',
                                                        color: '#ffffff',
                                                        '&:hover': {
                                                            backgroundColor: '#ff4858'
                                                        }
                                                    }}
                                                >
                                                    <RemoveRoundedIcon />
                                                </IconButton>
                                            </Stack>
                                        </Box>
                                    )) : (
                                        <Box
                                            sx={{
                                                minHeight: 118,
                                                borderRadius: '12px',
                                                border: '1px dashed rgba(129, 146, 178, 0.28)',
                                                backgroundColor: 'rgba(23, 34, 55, 0.44)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                px: 2
                                            }}
                                        >
                                            <Typography sx={{ color: '#8fa0c1', fontSize: '0.95rem', textAlign: 'center' }}>
                                                Add the camp coordinates you want to rotate through.
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>

                            <Helper>Rotation of camp targets [{'{X, Y}'}]. Kingdom is fixed to 0.</Helper>

                            <Box sx={{ mt: 2.1 }}>
                                <ToggleField
                                    checked={config.useFeathers}
                                    onChange={nextValue => update(nextConfig => { nextConfig.useFeathers = nextValue })}
                                    label="Use Feathers"
                                    helper="Use feathers for fast travel, if disabled it will use coin horses"
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} lg={5}>
                            <FieldTitle>Sleep Between Cycles (seconds)</FieldTitle>
                            <TextField
                                fullWidth
                                value={config.sleepBetweenCyclesSeconds}
                                onChange={event => update(nextConfig => { nextConfig.sleepBetweenCyclesSeconds = event.target.value })}
                                sx={inputSx}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                            <Helper>Pause after full camp rotation.</Helper>
                        </Grid>
                    </Grid>
                </Box>
            </Stack>
        </Box>
    )
}
